import React, { useState, useRef } from "react";
import { useLazyQuery } from "@apollo/client";
import Button from "components/base/Button";
import { InlineSpinner } from "components/base/Spinner";
import { Select } from "components/base/Form";
import { QUERY_ODOO } from "./graphql";
import { Alert } from "components/base/Toast";
import { splitAndFilter } from "./utils/helpers";
import { getModelConfig, availableModels } from "./utils/constants";

// 导入子组件
import FieldsSelector from "./components/FieldsSelector";
import IdsInput from "./components/IdsInput";
import ConditionsInput from "./components/ConditionsInput";
import ResultViewer from "./components/ResultViewer";

// Odoo 通用查询面板组件
const OdooModelQueryPanel = () => {
  // 状态
  const [result, setResult] = useState(null);
  const [modelName, setModelName] = useState("purchase.order");
  const [actionType, setActionType] = useState("search_read");
  const [orderId, setOrderId] = useState("");
  const [companyId, setCompanyId] = useState("3");
  const [queryTimeRange, setQueryTimeRange] = useState("twoWeeks"); // 默认查询两周
  const [isIdValid, setIsIdValid] = useState(true);
  const [customConditions, setCustomConditions] = useState([]);

  // 根据当前选择的模型获取配置
  const modelConfig = getModelConfig(modelName);

  const [selectedFields, setSelectedFields] = useState(
    modelConfig.defaultSelectedFields,
  );

  // 创建对子组件的引用
  const conditionsPanelRef = useRef(null);

  // 模型变更时重置相关状态
  React.useEffect(() => {
    // 重置字段选择
    setSelectedFields(getModelConfig(modelName).defaultSelectedFields);
    // 重置自定义条件
    setCustomConditions([]);
    // 重置结果
    setResult(null);
  }, [modelName]);

  // 定义查询
  const [queryOdooData, { loading, error }] = useLazyQuery(QUERY_ODOO, {
    fetchPolicy: "network-only",
    onCompleted: (data) => {
      console.log("GraphQL响应数据:", data); // 添加调试日志
      if (data?.queryOdoo) {
        try {
          setResult(JSON.parse(data.queryOdoo));
        } catch (e) {
          Alert("error", `解析返回数据出错: ${e.message}`);
        }
      } else {
        Alert("warning", "没有接收到有效的响应数据");
      }
    },
    onError: (error) => Alert("error", `查询出错: ${error.message}`),
  });

  // 构建搜索条件
  const buildSearchConditions = () => {
    const conditionsList = [];
    if (companyId.trim()) {
      conditionsList.push(["company_id", "=", parseInt(companyId)]);
    }

    customConditions.forEach((condition) => {
      conditionsList.push([
        condition.field,
        condition.operator,
        condition.value,
      ]);
    });

    if (customConditions.length === 0 && queryTimeRange !== "all") {
      // 根据选择的时间范围设置不同的日期
      let filterDate = new Date();
      if (queryTimeRange === "twoWeeks") {
        filterDate.setDate(filterDate.getDate() - 14); // 两周
      } else if (queryTimeRange === "month") {
        filterDate.setMonth(filterDate.getMonth() - 1); // 一个月
      }
      conditionsList.push([
        modelName === "purchase.order" ? "date_approve" : "create_date",
        ">=",
        filterDate.toISOString().split("T")[0],
      ]);
    }

    return JSON.stringify(
      conditionsList.length === 0
        ? [["id", "!=", false]]
        : conditionsList.length === 1
          ? conditionsList[0]
          : ["&", ...conditionsList],
    );
  };

  // 处理ID输入
  const processIds = (idInput) => {
    const idList = splitAndFilter(idInput);
    const validIds = [],
      invalidIds = [];

    idList.forEach((id) => {
      isNaN(parseInt(id)) ? invalidIds.push(id) : validIds.push(parseInt(id));
    });

    if (validIds.length === 0) {
      Alert("error", "没有找到任何有效的ID");
      return null;
    }

    if (invalidIds.length > 0) {
      Alert("warning", `以下ID格式不正确，将被忽略: ${invalidIds.join(", ")}`);
    }

    return validIds;
  };

  // 执行查询
  const handleQuery = () => {
    setResult(null);

    // 从FieldsSelector获取选中的字段
    const fields = Object.entries(selectedFields)
      .filter(([_, isSelected]) => isSelected)
      .map(([fieldName]) => fieldName);

    if (fields.length === 0) {
      Alert("error", "请至少选择一个字段");
      return;
    }

    if (actionType === "search_read") {
      // search_read模式 - 使用QueryConditionsPanel中的buildConditions
      if (conditionsPanelRef.current) {
        const conditions = conditionsPanelRef.current.buildConditions();
        queryOdooData({
          variables: {
            model: modelName,
            action: "search_read",
            fields,
            conditions,
          },
        });
      } else {
        // 使用自定义条件构建器
        const conditions = buildSearchConditions();
        queryOdooData({
          variables: {
            model: modelName,
            action: "search_read",
            fields,
            conditions,
          },
        });
      }
    } else {
      // read模式
      if (!orderId.trim()) {
        Alert("error", "请输入有效的ID");
        return;
      }

      if (!isIdValid) {
        Alert("error", "ID格式不正确");
        return;
      }

      // 处理多个ID
      const validIds = processIds(orderId);
      if (!validIds) return;

      queryOdooData({
        variables: {
          model: modelName,
          action: "read",
          fields,
          conditions: JSON.stringify(validIds),
        },
      });
    }
  };

  return (
    <div className="card bg-white shadow-md rounded-lg p-5">
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center space-x-2">
          <h2 className="text-xl font-bold">Odoo 数据查询</h2>
          <Select
            value={modelName}
            onChange={(e) => setModelName(e.target.value)}
            className="ml-4 w-48"
          >
            {availableModels.map((model) => (
              <option key={model.value} value={model.value}>
                {model.label}
              </option>
            ))}
          </Select>
        </div>
        <div className="flex items-center space-x-2">
          <span className="text-sm text-gray-600">操作类型:</span>
          <Select
            value={actionType}
            onChange={(e) => {
              setActionType(e.target.value);
              setIsIdValid(true); // 重置ID验证状态
            }}
            className="w-36"
          >
            <option value="search_read">search_read</option>
            <option value="read">read</option>
          </Select>
        </div>
      </div>

      <div className="mb-5">
        {/* 字段选择组件 */}
        <FieldsSelector
          modelName={modelName}
          selectedFields={selectedFields}
          setSelectedFields={setSelectedFields}
        />

        {/* Read模式下的ID输入组件 */}
        {actionType === "read" && (
          <IdsInput
            value={orderId}
            onChange={setOrderId}
            onValidChange={setIsIdValid}
            labelText={`${modelName} ID`}
          />
        )}

        {/* 查询条件组件 - 仅在search_read模式下显示 */}
        {actionType === "search_read" && (
          <ConditionsInput
            ref={conditionsPanelRef}
            modelName={modelName}
            companyId={companyId}
            setCompanyId={setCompanyId}
            queryTimeRange={queryTimeRange}
            setQueryTimeRange={setQueryTimeRange}
            customConditions={customConditions}
            setCustomConditions={setCustomConditions}
            defaultTimeFilterField={
              modelName === "purchase.order" ? "date_approve" : "create_date"
            }
          />
        )}

        {/* 查询按钮 */}
        <div className="flex justify-end">
          <Button
            primary
            onClick={handleQuery}
            disabled={loading || (actionType === "read" && !isIdValid)}
          >
            {loading ? (
              <span className="flex items-center">
                <InlineSpinner size={16} text={null} color="#fff" />
                <span className="ml-2">查询中...</span>
              </span>
            ) : (
              "查询"
            )}
          </Button>
        </div>
      </div>

      {/* 结果显示组件 */}
      <ResultViewer result={result} loading={loading} error={error} />
    </div>
  );
};

export default OdooModelQueryPanel;
